import { FunctionComponent } from "react";

import { InputField } from "@/components/InputField/InputField";

import { LeadDbConfig } from "../onboardingListTypes";
import { ConfigField, ConfigGrid, useConfigTranslation, useFieldError } from "./ConfigField";
import { ConfigFormProps } from "./configFormProps";
import { LookupMultiField, OptionMultiField, SuggestingTagField } from "./FilterFields";
import { resolveIndustries } from "./useOnboardingFilterOptions";

/** COMPANY_ENRICH / SEARCH — the person + company filters that make a database search targeted. */
export const LeadDbForm: FunctionComponent<ConfigFormProps<LeadDbConfig>> = ({
	value,
	errors,
	options,
	onChange,
}) => {
	const ts = useConfigTranslation();
	const errorFor = useFieldError(errors);

	return (
		<ConfigGrid>
			<SuggestingTagField
				error={errorFor("findTitles")}
				fetchFn={options.fetchPositions}
				label={ts("findTitles")}
				placeholder={ts("findTitlesPlaceholder")}
				testId="onboarding-leadDb-positions"
				values={value.positionQuery}
				onChange={(positionQuery) => onChange({ ...value, positionQuery })}
			/>

			<OptionMultiField
				label={ts("seniority")}
				options={options.seniorityOptions}
				placeholder={ts("seniorityPlaceholder")}
				values={value.seniority}
				onChange={(seniority) => onChange({ ...value, seniority })}
			/>

			<OptionMultiField
				label={ts("department")}
				options={options.departmentOptions}
				placeholder={ts("departmentPlaceholder")}
				values={value.department}
				onChange={(department) => onChange({ ...value, department })}
			/>

			<LookupMultiField
				fetchFn={options.fetchCountries}
				label={ts("personLocation")}
				placeholder={ts("personLocationPlaceholder")}
				testId="onboarding-leadDb-countries"
				titleOf={options.countryTitleOf}
				values={value.countries}
				onChange={(countries) => onChange({ ...value, countries })}
			/>

			<OptionMultiField
				label={ts("industries")}
				options={options.industryOptions}
				placeholder={ts("industriesPlaceholder")}
				values={value.industries.map((industry) => industry.name)}
				onChange={(names) =>
					onChange({ ...value, industries: resolveIndustries(names, options.industriesByName) })
				}
			/>

			<OptionMultiField
				label={ts("employees")}
				options={options.employeeOptions}
				placeholder={ts("employeesPlaceholder")}
				values={value.employees}
				onChange={(employees) => onChange({ ...value, employees })}
			/>

			<LookupMultiField
				fetchFn={options.fetchCompanyCountries}
				label={ts("companyHq")}
				placeholder={ts("companyHqPlaceholder")}
				testId="onboarding-leadDb-companyCountries"
				titleOf={options.countryTitleOf}
				values={value.companyCountries}
				onChange={(companyCountries) => onChange({ ...value, companyCountries })}
			/>

			<SuggestingTagField
				fetchFn={options.fetchKeywords}
				hint={ts("keywordsHint")}
				label={ts("keywords")}
				placeholder={ts("keywordsPlaceholder")}
				testId="onboarding-leadDb-keywords"
				values={value.keywords}
				onChange={(keywords) => onChange({ ...value, keywords })}
			/>

			<ConfigField
				full
				error={errorFor("semanticQuery")}
				hint={ts("semanticQueryHint")}
				label={ts("semanticQuery")}
			>
				<InputField
					error={Boolean(errors.semanticQuery)}
					handleChange={(event) => onChange({ ...value, semanticQuery: event.target.value })}
					name="leadDbSemanticQuery"
					placeholder={ts("semanticQueryPlaceholder")}
					showError={Boolean(errors.semanticQuery)}
					value={value.semanticQuery}
				/>
			</ConfigField>
		</ConfigGrid>
	);
};
